import { useMemo, useState } from "react";
import { Button } from "@mui/material";
import { useTranslations } from "use-intl";
import { useSingleFlightSelectionContext } from "@/app/lib/context/SingleFlightDetailsContext";
import { type Traveller } from "@/app/lib/types/types";
import { ExpandablePanel } from "./ExpandablePanel";
import { type MappedServiceData } from "./types";
import { getAvailableServices } from "@/app/lib/utils/additionalServices";
import { type AdditionalServiceData } from "@/app/lib/context/FlightSelectionPayloadContext/FlightSelectionPayloadContext";
import { Drawer, DrawerTopBar } from "../../Drawer";

type Props = {
  open: boolean;
  onClose: () => void;
  onConfirm: (
    travellerId: string,
    selectedServices: AdditionalServiceData[],
  ) => void;
  initialSelectedServices: AdditionalServiceData[];
  traveller: Traveller;
  travellerIndex: number | null;
};

export function AdditionalServicesSelectionDrawer({
  open,
  onClose,
  onConfirm,
  initialSelectedServices,
  traveller,
  travellerIndex,
}: Props) {
  const t = useTranslations();
  const { singleFlightDetails } = useSingleFlightSelectionContext();

  const [selectedServices, setSelectedServices] = useState<
    AdditionalServiceData[]
  >(initialSelectedServices);

  const serviceRouteMap = useMemo(() => {
    const services = getAvailableServices({
      traveller,
      travellerIndex,
      optionalServices: singleFlightDetails.optionalServices,
      mandatoryServices: singleFlightDetails.mandatoryServices,
    });

    const routes = services.reduce(
      (acc, serviceData) => {
        const routeKey = `${serviceData.service.route.departureCode} - ${serviceData.service.route.arrivalCode}`;
        if (!acc[routeKey]) {
          acc[routeKey] = [];
        }
        acc[routeKey].push(serviceData);
        return acc;
      },
      {} as Record<string, MappedServiceData[]>,
    );

    return routes;
  }, [
    singleFlightDetails.mandatoryServices,
    singleFlightDetails.optionalServices,
    traveller,
    travellerIndex,
  ]);

  return (
    <Drawer open={open} hasFullHeight heightOffset={100} onClose={onClose}>
      <DrawerTopBar
        onCloseClick={onClose}
        title={t("travellers.additionalServices")}
      />

      <div className="mb-auto flex flex-col gap-2 overflow-auto">
        {Object.entries(serviceRouteMap).map(([route, services]) => (
          <ExpandablePanel
            key={route}
            services={services}
            selectedServices={selectedServices}
            onServiceChange={(serviceData) => {
              setSelectedServices((prev) => {
                const newValue = prev.filter(
                  (s) =>
                    s.service.bookingCode !== serviceData.service.bookingCode,
                );

                if (serviceData.service.selectedOptionCode) {
                  newValue.push(serviceData);
                }

                return newValue;
              });
            }}
          />
        ))}
      </div>

      <Button
        fullWidth
        variant="contained"
        type="submit"
        onClick={() => onConfirm(traveller.id, selectedServices)}
        sx={{ mt: "calc(var(--spacing)*4)" }}
      >
        {t("confirm")}
      </Button>
    </Drawer>
  );
}
